import Input from "@/v2-ui/input"
import Select from "@/v2-ui/select"
import UserTenantTypePicker from "@/v2-backoffice-client/user/tenant/type/UserTenantTypePicker"
import { useUserTenantTypeQuery } from "@/v2-backoffice-client/user/tenant/type/user.tenant.type.query"
import { MINUTE_LIMIT_OPTIONS } from "@/v2-backoffice-client/invitation/invitation.constants"
import type { InvitationRow } from "@/v2-backoffice-client/invitation/invitation.constants"
import {
  parseMinuteLimit,
  parseUserTenantTypeCode,
} from "@/v2-backoffice-client/invitation/invitation.utils"
import BulkForm, {
  type BulkFormProps,
} from "@/v2-backoffice-client/components/bulkForm"
import ContactEmailStatusDot from "@/v2-backoffice-client/crm/ContactEmailStatusDot"
import type { ContactEmailChecks } from "@/v2-backoffice-client/crm/crm.contactChecks.hooks"

export type TenantInvitationCreateTableProps = BulkFormProps<InvitationRow> & {
  /** The live email check every row reads its HubSpot dot from. */
  checks: ContactEmailChecks
  createContact: boolean
}

function TenantInvitationCreateTable(props: TenantInvitationCreateTableProps) {
  const { rows, onUpdateRow, onRemoveRow, checks, createContact } = props

  const { data } = useUserTenantTypeQuery({ fetchPolicy: "cache-first" })
  const userTenantTypes = data?.getUserTenantTypes ?? []

  return (
    <BulkForm.Table>
      <thead>
        <tr>
          <th>Email *</th>
          <th>First Name</th>
          <th>Last Name</th>
          <th>Role *</th>
          <th>Expires</th>
          <th />
        </tr>
      </thead>
      <tbody>
        {rows.map((row, index) => (
          <tr key={row.key}>
            <td data-label="Email" data-full-width className="relative">
              <Input
                type="email"
                className="pr-7"
                required
                placeholder="user@example.com"
                value={row.Email}
                onChange={(e) => {
                  onUpdateRow(index, "Email", e.target.value)
                }}
              />
              <ContactEmailStatusDot
                email={row.Email}
                checks={checks}
                createContact={createContact}
              />
            </td>
            <td data-label="First Name">
              <Input
                type="text"
                required
                value={row.FirstName}
                onChange={(e) => {
                  onUpdateRow(index, "FirstName", e.target.value)
                }}
              />
            </td>
            <td data-label="Last Name">
              <Input
                type="text"
                required
                value={row.LastName}
                onChange={(e) => {
                  onUpdateRow(index, "LastName", e.target.value)
                }}
              />
            </td>
            <td data-label="Role">
              <UserTenantTypePicker
                className="w-full text-xs"
                align="between"
                variant="outline"
                size="sm"
                iconSrc="angle-small-down"
                iconPosition="right"
                iconClassName="text-muted-foreground"
                value={row.UserTenantTypeCode}
                onChange={(code) => {
                  onUpdateRow(index, "UserTenantTypeCode", code)
                }}
              />
              <BulkForm.PasteSlot
                onPaste={(text) => {
                  const code = parseUserTenantTypeCode(text, userTenantTypes)
                  if (code !== undefined) {
                    onUpdateRow(index, "UserTenantTypeCode", code)
                  }
                }}
              />
            </td>
            <td data-label="Expires">
              <Select
                value={String(row.MinuteLimit)}
                onValueChange={(val) => {
                  onUpdateRow(index, "MinuteLimit", Number.parseInt(val, 10))
                }}
              >
                <Select.Trigger size="xs">
                  <Select.Value />
                </Select.Trigger>
                <Select.Content>
                  {MINUTE_LIMIT_OPTIONS.map((opt) => (
                    <Select.Item key={opt.value} value={String(opt.value)}>
                      {opt.label}
                    </Select.Item>
                  ))}
                </Select.Content>
              </Select>
              <BulkForm.PasteSlot
                onPaste={(text) => {
                  const minutes = parseMinuteLimit(text)
                  if (minutes !== undefined) {
                    onUpdateRow(index, "MinuteLimit", minutes)
                  }
                }}
              />
            </td>
            <BulkForm.RemoveCell
              onRemove={() => {
                onRemoveRow(index)
              }}
            />
          </tr>
        ))}
      </tbody>
    </BulkForm.Table>
  )
}

export default TenantInvitationCreateTable
